/**
 * EnhancedExecutiveSummary Component
 * Industry-grade executive summary with stunning visualizations
 * Features: Animated KPI gauges, trend sparklines, AI confidence indicators
 */

import { useMemo } from 'react'
import Plot from 'react-plotly.js'
import {
  TrendingUp,
  TrendingDown,
  Shield,
  Clock,
  Target,
  Award,
  AlertTriangle,
  CheckCircle,
  Zap,
  Brain,
  Activity
} from 'lucide-react'
import clsx from 'clsx'

interface KPIMetric {
  label: string
  value: number
  previousValue?: number
  unit: string
  target?: number
  format?: 'percent' | 'currency' | 'number' | 'hours'
  trend?: number[]
}

interface EnhancedExecutiveSummaryProps {
  period: string
  overallScore: number
  previousScore?: number
  aiConfidence: number
  metrics: {
    reliability: KPIMetric
    availability: KPIMetric
    mtbf: KPIMetric
    mttr: KPIMetric
    costSavings: KPIMetric
    predictedFailures: KPIMetric
  }
  highlights: {
    achievements: string[]
    concerns: string[]
    recommendations: string[]
  }
  modelPerformance: {
    accuracy: number
    precision: number
    recall: number
    f1Score: number
  }
  className?: string
}

export default function EnhancedExecutiveSummary({
  period,
  overallScore,
  previousScore,
  aiConfidence,
  metrics,
  highlights,
  modelPerformance,
  className
}: EnhancedExecutiveSummaryProps) {
  const scoreChange = previousScore ? overallScore - previousScore : 0
  const isImproving = scoreChange > 0

  const formatValue = (value: number, format?: string, unit?: string) => {
    switch (format) {
      case 'percent':
        return `${value.toFixed(1)}%`
      case 'currency':
        return `$${(value / 1000).toFixed(0)}K`
      case 'hours':
        return `${value.toFixed(1)}h`
      default:
        return `${value.toFixed(0)}${unit || ''}`
    }
  }

  const getScoreColor = (score: number) => {
    if (score >= 90) return '#10b981'
    if (score >= 75) return '#f59e0b'
    return '#ef4444'
  }

  // Gauge chart for overall score
  const gaugeData = useMemo(() => [{
    type: 'indicator' as const,
    mode: 'gauge+number+delta' as const,
    value: overallScore,
    delta: { reference: previousScore || overallScore, valueformat: '.1f' },
    title: { text: 'Overall Performance', font: { size: 14, color: '#94a3b8' } },
    number: { suffix: '%', font: { size: 32, color: '#f8fafc' } },
    gauge: {
      axis: { range: [0, 100], tickcolor: '#475569', tickwidth: 2 },
      bar: { color: getScoreColor(overallScore), thickness: 0.75 },
      bgcolor: '#1e293b',
      borderwidth: 0,
      steps: [
        { range: [0, 60], color: 'rgba(239, 68, 68, 0.1)' },
        { range: [60, 80], color: 'rgba(245, 158, 11, 0.1)' },
        { range: [80, 100], color: 'rgba(16, 185, 129, 0.1)' }
      ],
      threshold: {
        line: { color: '#3b82f6', width: 3 },
        thickness: 0.8,
        value: 85
      }
    }
  }], [overallScore, previousScore])

  // Radar chart for model performance
  const radarData = useMemo(() => [{
    type: 'scatterpolar' as const,
    r: [
      modelPerformance.accuracy * 100,
      modelPerformance.precision * 100,
      modelPerformance.recall * 100,
      modelPerformance.f1Score * 100,
      modelPerformance.accuracy * 100
    ],
    theta: ['Accuracy', 'Precision', 'Recall', 'F1 Score', 'Accuracy'],
    fill: 'toself' as const,
    fillcolor: 'rgba(59, 130, 246, 0.2)',
    line: { color: '#3b82f6', width: 2 },
    name: 'Model Performance'
  }], [modelPerformance])

  return (
    <div className={clsx('rounded-2xl border border-surface-700 bg-gradient-to-br from-surface-900 via-surface-900 to-surface-800 overflow-hidden', className)}>
      {/* Header with Gradient */}
      <div className="relative p-6 bg-gradient-to-r from-primary-600/20 via-purple-600/10 to-transparent border-b border-surface-700">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-primary-500/10 via-transparent to-transparent" />
        <div className="relative flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 shadow-lg shadow-primary-500/25">
                <Award size={24} className="text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-surface-100">Executive Summary</h2>
                <p className="text-sm text-surface-400">{period}</p>
              </div>
            </div>
          </div>

          {/* AI Confidence Badge */}
          <div className="flex flex-col items-end">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-surface-800/80 border border-surface-700">
              <Brain size={14} className="text-primary-400" />
              <span className="text-xs text-surface-400">AI Confidence</span>
              <span className="text-sm font-mono font-bold text-primary-400">{(aiConfidence * 100).toFixed(0)}%</span>
            </div>
            <div className="mt-2 flex items-center gap-1">
              <div className={clsx(
                'w-2 h-2 rounded-full animate-pulse',
                aiConfidence >= 0.9 ? 'bg-status-healthy' :
                aiConfidence >= 0.7 ? 'bg-status-warning' : 'bg-status-critical'
              )} />
              <span className="text-xs text-surface-500">
                {aiConfidence >= 0.9 ? 'High Reliability' :
                 aiConfidence >= 0.7 ? 'Moderate Reliability' : 'Review Recommended'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Score Gauge & Model Performance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6 border-b border-surface-700">
        {/* Overall Performance Gauge */}
        <div className="flex flex-col items-center">
          <Plot
            data={gaugeData as any}
            layout={{
              width: 280,
              height: 200,
              margin: { t: 40, r: 25, l: 25, b: 20 },
              paper_bgcolor: 'transparent',
              font: { color: '#94a3b8', family: 'Inter, sans-serif' }
            }}
            config={{ displayModeBar: false, responsive: true }}
          />
          <div className="flex items-center gap-2 mt-2">
            {isImproving ? (
              <TrendingUp size={16} className="text-status-healthy" />
            ) : (
              <TrendingDown size={16} className="text-status-critical" />
            )}
            <span className={clsx(
              'text-sm font-medium',
              isImproving ? 'text-status-healthy' : 'text-status-critical'
            )}>
              {isImproving ? '+' : ''}{scoreChange.toFixed(1)}% vs previous period
            </span>
          </div>
        </div>

        {/* Model Performance Radar */}
        <div className="flex flex-col items-center">
          <h4 className="text-xs font-semibold text-surface-400 uppercase mb-2">AI Model Performance</h4>
          <Plot
            data={radarData as any}
            layout={{
              width: 280,
              height: 200,
              margin: { t: 30, r: 50, l: 50, b: 30 },
              paper_bgcolor: 'transparent',
              polar: {
                bgcolor: 'transparent',
                radialaxis: {
                  visible: true,
                  range: [0, 100],
                  gridcolor: '#334155',
                  tickfont: { size: 9, color: '#64748b' }
                },
                angularaxis: {
                  gridcolor: '#334155',
                  tickfont: { size: 10, color: '#94a3b8' }
                }
              },
              showlegend: false
            }}
            config={{ displayModeBar: false, responsive: true }}
          />
        </div>
      </div>

      {/* KPI Cards Grid */}
      <div className="p-6 border-b border-surface-700">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-4">Key Performance Indicators</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Object.entries(metrics).map(([key, metric]) => {
            const change = metric.previousValue ? metric.value - metric.previousValue : 0
            const isPositive = key === 'mttr' ? change < 0 : change > 0

            return (
              <div
                key={key}
                className="p-4 rounded-xl bg-surface-800/50 border border-surface-700 hover:border-primary-500/30 transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-surface-400">{metric.label}</span>
                  {metric.previousValue && (
                    <span className={clsx(
                      'text-xs font-mono',
                      isPositive ? 'text-status-healthy' : 'text-status-critical'
                    )}>
                      {isPositive ? '+' : ''}{change.toFixed(1)}{metric.unit}
                    </span>
                  )}
                </div>
                <p className="text-2xl font-mono font-bold text-surface-100 group-hover:text-primary-400 transition-colors">
                  {formatValue(metric.value, metric.format, metric.unit)}
                </p>
                {metric.target && (
                  <div className="mt-2">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-surface-500">Target</span>
                      <span className="text-surface-400">{formatValue(metric.target, metric.format, metric.unit)}</span>
                    </div>
                    <div className="h-1.5 bg-surface-700 rounded-full overflow-hidden">
                      <div
                        className={clsx(
                          'h-full rounded-full transition-all',
                          metric.value >= metric.target ? 'bg-status-healthy' : 'bg-primary-500'
                        )}
                        style={{ width: `${Math.min((metric.value / metric.target) * 100, 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Highlights Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-surface-700">
        {/* Achievements */}
        <div className="p-5 bg-surface-900">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-status-healthy/20">
              <CheckCircle size={16} className="text-status-healthy" />
            </div>
            <h4 className="font-semibold text-surface-200">Key Achievements</h4>
          </div>
          <ul className="space-y-2">
            {highlights.achievements.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                <Zap size={14} className="text-status-healthy mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Concerns */}
        <div className="p-5 bg-surface-900">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-status-warning/20">
              <AlertTriangle size={16} className="text-status-warning" />
            </div>
            <h4 className="font-semibold text-surface-200">Areas of Concern</h4>
          </div>
          <ul className="space-y-2">
            {highlights.concerns.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                <Activity size={14} className="text-status-warning mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Recommendations */}
        <div className="p-5 bg-surface-900">
          <div className="flex items-center gap-2 mb-4">
            <div className="p-1.5 rounded-lg bg-primary-500/20">
              <Target size={16} className="text-primary-400" />
            </div>
            <h4 className="font-semibold text-surface-200">AI Recommendations</h4>
          </div>
          <ul className="space-y-2">
            {highlights.recommendations.map((item, index) => (
              <li key={index} className="flex items-start gap-2 text-sm text-surface-300">
                <Shield size={14} className="text-primary-400 mt-0.5 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Footer with Timestamp */}
      <div className="px-6 py-3 bg-surface-800/50 border-t border-surface-700 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-surface-500">
          <Brain size={12} />
          <span>Analysis powered by Multi-Model AI Engine</span>
        </div>
        <div className="flex items-center gap-2 text-xs text-surface-500">
          <Clock size={12} />
          <span>Generated: {new Date().toLocaleString()}</span>
        </div>
      </div>
    </div>
  )
}
